import { Link, NavLink } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/hooks/use-auth";
import { useProfile } from "@/hooks/use-profile";
import { ThemeToggle } from "@/components/shared/ThemeToggle";

const NavItem = ({ to, label }: { to: string; label: string }) => (
  <NavLink
    to={to}
    className={({ isActive }) =>
      [
        "px-4 py-2 rounded-full text-sm font-medium transition-all duration-200",
        isActive 
          ? "bg-primary/10 text-primary font-semibold" 
          : "text-muted-foreground hover:text-foreground hover:bg-secondary/50",
      ].join(" ")
    }
  >
    {label}
  </NavLink>
);

const Navbar = () => {
  const { user, logout } = useAuth();
  const { profile } = useProfile();

  const avatarSrc =
    profile?.avatar_url ||
    (user as any)?.user_metadata?.avatar_url ||
    null;

  const isAdmin = profile?.role === "admin";

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/80 backdrop-blur-md supports-[backdrop-filter]:bg-background/60 shrink-0">
      <div className="container mx-auto h-16 flex items-center justify-between px-4">
        <div className="flex items-center gap-8">
          <Link to="/" className="flex items-center gap-2">
            <span className="font-bold text-xl tracking-tight bg-gradient-to-r from-primary to-orange-600 bg-clip-text text-transparent">
              Código 31
            </span>
          </Link>
          
          <nav className="hidden md:flex items-center gap-1 bg-secondary/30 p-1 rounded-full border border-border/50">
            <NavItem to="/chat" label="Chat" />
            
            {/* Clientes agora é apenas para ADMIN */}
            {isAdmin && (
              <>
                <NavItem to="/clients" label="Clientes" />
                <NavItem to="/admin/users" label="Usuários" />
                <NavItem to="/admin/conversations" label="Conversas" />
                <NavItem to="/admin/analytics" label="Analytics" />
              </>
            )}
          </nav>
        </div>

        <div className="flex items-center gap-4">
          <ThemeToggle />

          {!user && (
            <div className="flex items-center gap-2">
              <Button asChild variant="ghost" className="rounded-full hidden sm:flex">
                <Link to="/login">Entrar</Link>
              </Button>
              <Button asChild className="rounded-full px-6">
                <Link to="/signup">Criar conta</Link>
              </Button>
            </div>
          )}
          
          {user && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="rounded-full h-9 w-9 ring-2 ring-transparent hover:ring-primary/20 transition-all">
                  <Avatar className="h-9 w-9 border">
                    {avatarSrc ? (
                      <AvatarImage src={avatarSrc} alt="Avatar" />
                    ) : null}
                    <AvatarFallback className="bg-primary/10 text-primary font-bold">
                      {user?.email?.[0]?.toUpperCase() || "U"}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 p-2">
                <div className="px-2 py-1.5 text-sm">
                  <div className="font-semibold truncate">{profile?.full_name || "Usuário"}</div>
                  <div className="text-xs text-muted-foreground truncate">{user.email}</div>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild className="rounded-md cursor-pointer">
                  <Link to="/profile">Meu Perfil</Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => logout()} className="text-destructive focus:text-destructive rounded-md cursor-pointer">
                  Sair da conta
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
    </header>
  );
};

export default Navbar;